import type {
  AutoscaleInfo, IChartApiBase, IPrimitivePaneRenderer, IPrimitivePaneView, ISeriesApi, ISeriesPrimitive,
  Logical, PrimitiveHoveredItem, SeriesAttachedParameter, SeriesType, Time,
} from "lightweight-charts";
import { ENTRY_WINDOW_SEC, barIndexAt, logicalAt, planReward, resultTag, resultTone, type ChartSignal, type SetupZone, type Tone } from "./chartSignals";

/**
 * Series primitive that draws chart signals the way a trader marks them up by
 * hand (TradingView's long/short position tool):
 *
 *  - a BUY/SELL tag on the candle whose close fired it, with the result beside it
 *  - shaded boxes from entry until the trade ended: red between entry and stop
 *    (the risk), green between entry and target 2 (the reward)
 *  - dashed stop and target lines; the stop line ends where TP1 moved it to entry
 *  - dots where TP1 was hit and where the trade closed
 *  - SL / Entry / TP1 / TP2 price tags on the hovered or selected signal
 *  - the zone the coin is waiting for (desk stage Waiting / Almost) and its stop
 *
 * Boxes draw behind the candles, everything else above the candles and EMAs.
 * Solid BUY/SELL tags were sent to Telegram or are live on the desk; light
 * ones are replays of today's rules on past candles.
 */

type Target = Parameters<IPrimitivePaneRenderer["draw"]>[0];
type Ctx = CanvasRenderingContext2D;

export interface OverlayColors { profit: string; loss: string; text: string; muted: string; surface: string; font: string }
export interface OverlayBar { time: number; high: number; low: number }

export interface SignalFocus {
  /** Drawn stronger than the rest. */
  emphasis: string | null;
  /** Gets SL / Entry / TP price tags (the hovered or selected signal). */
  tags: string | null;
  /** Kept fully in view: the price scale widens to its stop and targets. */
  fit: string | null;
}

/** Pane areas that HTML or the chart's own labels cover; price tags keep out. */
export interface TagInsets {
  /** Px at the right edge where price-line titles sit. */
  rightLane: number;
  /** The legend box (pane px): a tag under it drops below it. */
  legend: Rect | null;
}

const PILL_H = 18;
const PILL_PAD = 6;
const POINTER = 5;
const GAP = 4;
const TAG_H = 16;
const TAG_PAD = 5;
/** Open trades run this many bars past the last candle, into the empty space. */
const EXTEND_BARS = 2;
/** Hit-test id of the waiting zone (signals use their own ids). */
export const SETUP_ID = "setup";

/** Positions in logical bar units: they change with the data, not with scrolling. */
interface Layout {
  sig: ChartSignal;
  lSig: number;            // signal moment (the trigger candle's close)
  lStart: number;          // fill: the boxes start here
  lEnd: number;            // right edge of the bar the trade ended in, or past the last candle
  lRisk: number;           // the risk box ends at TP1 (stop moved to entry) or at lEnd
  lTp1: number | null;     // centre of the bar where TP1 was hit
  lExit: number | null;    // centre of the bar where the trade closed
  lChase: number | null;   // end of the entry window, while the don't-chase line still applies
  trigger: { idx: number; hi: number; lo: number } | null; // null: trigger candle not loaded
}

export interface Rect { x: number; y: number; w: number; h: number }

/** Pixel positions, recomputed whenever the chart scrolls, zooms or resizes. */
interface Geometry {
  sig: ChartSignal;
  focused: boolean;
  xSig: number; xStart: number; xEnd: number; xRisk: number;
  xTp1: number | null; xExit: number | null;
  yEntry: number; ySl: number; yTp1: number; yTp2: number; yExit: number | null;
  /** The don't-chase line: from the signal to the end of its entry window. */
  xChase: number | null; yChase: number | null;
  box: Rect;
  pill: (Rect & { split: number; tipX: number; down: boolean; left: string; right: string }) | null;
}

// ─── canvas helpers ──────────────────────────────────────────────────────────

let measureCtx: Ctx | null = null;
function textWidth(text: string, font: string): number {
  measureCtx ??= document.createElement("canvas").getContext("2d");
  if (!measureCtx) return text.length * 6.5;
  measureCtx.font = font;
  return measureCtx.measureText(text).width;
}

function rgb(color: string): [number, number, number] | null {
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (m) {
    const [r, g, b] = m[1]!.split(",").map(v => Number(v.trim()));
    return [r ?? 0, g ?? 0, b ?? 0];
  }
  const h = color.match(/^#([0-9a-f]{6})$/i);
  if (h) {
    const n = parseInt(h[1]!, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  return null;
}

function withAlpha(color: string, alpha: number): string {
  const c = rgb(color);
  return c ? `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${alpha})` : color;
}

/** Dark ink on light fills (lime / coral in the dark theme), white on dark ones. */
function inkOn(color: string): string {
  const c = rgb(color);
  if (!c) return "#ffffff";
  return (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255 > 0.6 ? "#0b1416" : "#ffffff";
}

function fillBetween(ctx: Ctx, x1: number, x2: number, y1: number, y2: number) {
  ctx.fillRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
}

function hLine(ctx: Ctx, x1: number, x2: number, y: number, color: string, width: number, dash: number[]) {
  if (x2 <= x1) return;
  const yy = Math.round(y) + 0.5;
  ctx.beginPath();
  ctx.setLineDash(dash);
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.moveTo(x1, yy);
  ctx.lineTo(x2, yy);
  ctx.stroke();
  ctx.setLineDash([]);
}

function dot(ctx: Ctx, x: number, y: number, color: string, ring: string, r = 3) {
  ctx.beginPath();
  ctx.arc(x, y, r + 1.5, 0, Math.PI * 2);
  ctx.fillStyle = ring;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
}

const inside = (r: Rect, x: number, y: number) => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

// ─── primitive ───────────────────────────────────────────────────────────────

export class SignalOverlay implements ISeriesPrimitive<Time> {
  private chart: IChartApiBase<Time> | null = null;
  private series: ISeriesApi<SeriesType, Time> | null = null;
  private requestUpdate: (() => void) | null = null;
  private times: number[] = [];
  private tfSec = 3600;
  private layout: Layout[] = [];
  private geoms: Geometry[] = [];
  private setup: SetupZone | null = null;
  private setupGeom: {
    x: number; yLow: number; yHigh: number; yStop: number | null;
    yEntry: number | null; yTp1: number | null; yTp2: number | null;
  } | null = null;
  /**
   * Hovering or clicking the zone opens its card. The band comes from the
   * view update (it runs on every mouse move, before the hit test); the tags
   * are only known once drawn, so the last draw's are kept.
   */
  private setupBand: Rect | null = null;
  private setupTagHits: Rect[] = [];
  private focus: SignalFocus = { emphasis: null, tags: null, fit: null };
  private insets: TagInsets = { rightLane: 0, legend: null };
  private readonly views: readonly IPrimitivePaneView[];

  constructor(private readonly colors: OverlayColors, private readonly format: (price: number) => string) {
    // Boxes in the background pass, under the candles. Marks in the top layer,
    // so the EMA lines (series added after the candles) don't cross the tags.
    const zones: IPrimitivePaneRenderer = { draw: () => {}, drawBackground: target => this.drawZones(target) };
    const marks: IPrimitivePaneRenderer = { draw: target => this.drawMarks(target) };
    this.views = [
      { zOrder: () => "normal", renderer: () => zones },
      { zOrder: () => "top", renderer: () => marks },
    ];
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>) {
    this.chart = chart;
    this.series = series;
    this.requestUpdate = requestUpdate;
  }

  detached() {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  paneViews() {
    return this.views;
  }

  /** Signals to draw, against the candles on the chart (ascending, seconds). */
  setData(signals: ChartSignal[], bars: OverlayBar[], tfSec: number) {
    const times = bars.map(b => b.time);
    this.times = times;
    this.tfSec = tfSec;
    const last = times.length - 1;
    const at = (t: number) => logicalAt(times, tfSec, t);
    const centre = (t: number) => Math.floor(at(t) + 0.5);
    const pastLast = last + 0.5 + EXTEND_BARS;

    this.layout = last < 0 ? [] : signals.map(sig => {
      const lSig = at(sig.signalTime);
      const lStart = sig.fillTime !== null ? at(sig.fillTime) : lSig;
      let lEnd: number;
      if (sig.exitTime !== null) lEnd = centre(sig.exitTime) + 0.5;
      else if (sig.result === "no-fill") lEnd = Math.min(pastLast, centre(sig.signalTime + 6 * 3600 - 1) + 0.5);
      else lEnd = pastLast;
      lEnd = Math.max(lEnd, lSig + 1); // at least one bar wide
      const lTp1 = sig.tp1Time !== null ? centre(sig.tp1Time) : null;

      // The 1h candle that fired it (several bars on 5m/15m). Needs to be loaded.
      let trigger: Layout["trigger"] = null;
      if (sig.signalTime - 1 >= times[0]! && sig.signalTime - 1 < times[last]! + tfSec) {
        const from = barIndexAt(times, tfSec, sig.signalTime - 3600);
        const to = barIndexAt(times, tfSec, sig.signalTime - 1);
        let hi = -Infinity, lo = Infinity;
        for (let i = from; i <= to; i++) { hi = Math.max(hi, bars[i]!.high); lo = Math.min(lo, bars[i]!.low); }
        trigger = { idx: to, hi, lo };
      }

      // The don't-chase line lives while the entry window is open and the
      // trade is not over: past it, an entry pays a worse price for the same stop.
      const windowEnd = sig.signalTime + ENTRY_WINDOW_SEC;
      const chaseLive = sig.chase != null && Date.now() / 1000 < windowEnd && (sig.result === "pending" || sig.result === "open");

      return {
        sig, lSig, lStart, lEnd,
        lRisk: lTp1 !== null ? Math.min(lEnd, lTp1 + 0.5) : lEnd,
        lTp1,
        lExit: sig.exitTime !== null ? centre(sig.exitTime) : null,
        lChase: chaseLive ? Math.max(lSig + 1, Math.min(pastLast, at(windowEnd))) : null,
        trigger,
      };
    });
    this.requestUpdate?.();
  }

  setFocus(focus: SignalFocus) {
    const f = this.focus;
    if (f.emphasis === focus.emphasis && f.tags === focus.tags && f.fit === focus.fit) return;
    this.focus = focus;
    this.requestUpdate?.();
  }

  setInsets(insets: TagInsets) {
    this.insets = insets;
    this.requestUpdate?.();
  }

  /** The zone the coin is waiting for, drawn from the 1h close it was read on to the right edge. */
  setSetup(zone: SetupZone | null) {
    this.setup = zone;
    this.requestUpdate?.();
  }

  /** Logical bar span of a signal, for scrolling it into view. */
  spanOf(id: string): { from: number; to: number } | null {
    const l = this.layout.find(l => l.sig.id === id);
    return l ? { from: Math.min(l.lSig, l.lStart), to: l.lEnd } : null;
  }

  /** Keeps the selected signal's whole plan, and the zone being waited for, in view. */
  autoscaleInfo(start: Logical, end: Logical): AutoscaleInfo | null {
    const prices: number[] = [];
    const l = this.focus.fit ? this.layout.find(l => l.sig.id === this.focus.fit) : undefined;
    if (l && l.lEnd >= start && l.lSig <= end) prices.push(l.sig.entry, l.sig.stopLoss, l.sig.tp2);
    const z = this.setup;
    if (z && this.times.length > 0 && logicalAt(this.times, this.tfSec, z.fromSec) <= end) {
      prices.push(z.low, z.high);
      if (z.stop !== null) prices.push(z.stop);
      if (z.plan) prices.push(z.plan.tp1); // target 2 may be far: it pins to the edge instead
    }
    return prices.length > 0 ? { priceRange: { minValue: Math.min(...prices), maxValue: Math.max(...prices) } } : null;
  }

  hitTest(x: number, y: number): PrimitiveHoveredItem | null {
    for (let i = this.geoms.length - 1; i >= 0; i--) {
      const p = this.geoms[i]!.pill;
      // The tag plus its pointer.
      if (p && inside({ x: p.x, y: p.down ? p.y : p.y - POINTER, w: p.w, h: p.h + POINTER }, x, y)) {
        return { externalId: this.geoms[i]!.sig.id, zOrder: "top", cursorStyle: "pointer" };
      }
    }
    for (let i = this.geoms.length - 1; i >= 0; i--) {
      const g = this.geoms[i]!;
      if (inside(g.box, x, y)) return { externalId: g.sig.id, zOrder: "normal", isBackground: true };
    }
    if ((this.setupBand && inside(this.setupBand, x, y)) || this.setupTagHits.some(r => inside(r, x, y))) {
      return { externalId: SETUP_ID, zOrder: "top", cursorStyle: "pointer" };
    }
    return null;
  }

  updateAllViews() {
    this.geoms = [];
    this.setupGeom = null;
    this.setupBand = null;
    const chart = this.chart, series = this.series;
    if (!chart || !series || this.times.length === 0) return;
    const ts = chart.timeScale();
    const x0 = ts.logicalToCoordinate(0 as Logical);
    const x1 = ts.logicalToCoordinate(1 as Logical);
    if (x0 === null || x1 === null) return;
    // logicalToCoordinate only takes whole bars; positions are linear in between.
    const xAt = (l: number) => x0 + l * (x1 - x0);
    const yAt = (price: number) => series.priceToCoordinate(price);
    const pillFont = `700 10px ${this.colors.font}`;
    const resultFont = `600 10px ${this.colors.font}`;

    const z = this.setup;
    if (z) {
      const yLow = yAt(z.low), yHigh = yAt(z.high);
      if (yLow !== null && yHigh !== null) {
        const x = xAt(logicalAt(this.times, this.tfSec, z.fromSec));
        this.setupGeom = {
          x, yLow, yHigh,
          yStop: z.stop !== null ? yAt(z.stop) : null,
          yEntry: z.plan ? yAt(z.plan.entry) : null,
          yTp1: z.plan ? yAt(z.plan.tp1) : null,
          yTp2: z.plan ? yAt(z.plan.tp2) : null,
        };
        // To the right edge, whatever the pane width.
        this.setupBand = { x, y: Math.min(yLow, yHigh), w: Number.MAX_SAFE_INTEGER, h: Math.abs(yHigh - yLow) };
      }
    }

    for (const l of this.layout) {
      const s = l.sig;
      const yEntry = yAt(s.entry), ySl = yAt(s.stopLoss), yTp1 = yAt(s.tp1), yTp2 = yAt(s.tp2);
      if (yEntry === null || ySl === null || yTp1 === null || yTp2 === null) continue;
      const xSig = xAt(l.lSig), xEnd = xAt(l.lEnd);
      const yTop = Math.min(yEntry, ySl, yTp2), yBottom = Math.max(yEntry, ySl, yTp2);

      let pill: Geometry["pill"] = null;
      if (l.trigger) {
        const down = s.action === "SHORT"; // SELL sits above the candle, pointing down
        const extreme = yAt(down ? l.trigger.hi : l.trigger.lo);
        // Px from the candle out to the stop. A stop just beyond the candle:
        // sit past the stop line (outside the risk box) instead of covering
        // it. A far stop would detach the tag from its candle.
        const stopGap = extreme === null ? null : down ? extreme - ySl : ySl - extreme;
        const anchor = stopGap !== null && stopGap > 0 && stopGap <= 40 ? ySl : extreme;
        if (anchor !== null) {
          const left = `${s.source === "mine" ? "YOU " : ""}${s.action === "LONG" ? "BUY" : "SELL"}`;
          const right = resultTag(s);
          const lw = textWidth(left, pillFont) + 2 * PILL_PAD;
          const rw = right ? textWidth(right, resultFont) + 2 * PILL_PAD : 0;
          const tipX = xAt(l.trigger.idx);
          const y = down ? anchor - GAP - POINTER - PILL_H : anchor + GAP + POINTER;
          pill = { x: tipX - lw / 2, y, w: lw + rw, h: PILL_H, split: lw, tipX, down, left, right };
        }
      }

      const yExit = s.exitPrice !== null ? yAt(s.exitPrice) : null;
      const yChase = l.lChase !== null && s.chase != null ? yAt(s.chase) : null;
      this.geoms.push({
        sig: s,
        focused: s.id === this.focus.emphasis,
        xSig, xStart: xAt(l.lStart), xEnd, xRisk: xAt(l.lRisk),
        xTp1: l.lTp1 !== null ? xAt(l.lTp1) : null,
        xExit: l.lExit !== null ? xAt(l.lExit) : null,
        yEntry, ySl, yTp1, yTp2, yExit,
        xChase: l.lChase !== null && yChase !== null ? xAt(l.lChase) : null,
        yChase,
        box: { x: Math.min(xSig, xAt(l.lStart)), y: yTop, w: xEnd - Math.min(xSig, xAt(l.lStart)), h: yBottom - yTop },
        pill,
      });
    }
  }

  // ─── drawing ───────────────────────────────────────────────────────────────

  /** Focused last, so it draws on top of its neighbours. */
  private ordered(): Geometry[] {
    return [...this.geoms.filter(g => !g.focused), ...this.geoms.filter(g => g.focused)];
  }

  private toneColor(tone: Tone): string {
    return tone === "profit" ? this.colors.profit : tone === "loss" ? this.colors.loss : this.colors.text;
  }

  private drawZones(target: Target) {
    if (this.geoms.length === 0 && !this.setupGeom) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const s = this.setupGeom;
      if (s && this.setup) {
        // The plan as a long / short position box, like a Buy / Sell plan
        // drawing: red from the entry to the stop, green to target 2.
        const planned = this.setup.plan && s.yEntry !== null;
        // Same fill and outline as a drawn plan's boxes (drawing/renderers posZone).
        const box = (yTo: number, color: string) => {
          ctx.fillStyle = withAlpha(color, 0.14);
          fillBetween(ctx, s.x, mediaSize.width, s.yEntry!, yTo);
          ctx.strokeStyle = withAlpha(color, 0.6);
          ctx.lineWidth = 1;
          ctx.strokeRect(Math.round(s.x) + 0.5, Math.round(Math.min(s.yEntry!, yTo)) + 0.5, mediaSize.width - s.x, Math.round(Math.abs(yTo - s.yEntry!)));
        };
        if (planned && s.yStop !== null) box(s.yStop, this.colors.loss);
        if (planned && s.yTp2 !== null) box(s.yTp2, this.colors.profit);
        // The zone itself: only its dashed edges over a box, shaded without one.
        if (!planned) {
          ctx.fillStyle = withAlpha(this.setup.direction === "LONG" ? this.colors.profit : this.colors.loss, 0.1);
          fillBetween(ctx, s.x, mediaSize.width, s.yLow, s.yHigh);
        }
      }
      for (const g of this.ordered()) {
        if (g.sig.result === "no-fill") continue; // no trade, nothing was at risk
        const quiet = g.sig.result === "pending";
        ctx.fillStyle = withAlpha(this.colors.loss, g.focused ? 0.16 : quiet ? 0.05 : 0.08);
        fillBetween(ctx, g.xStart, g.xRisk, g.yEntry, g.ySl);
        ctx.fillStyle = withAlpha(this.colors.profit, g.focused ? 0.13 : quiet ? 0.04 : 0.07);
        fillBetween(ctx, g.xStart, g.xEnd, g.yEntry, g.yTp2);
      }
    });
  }

  private drawMarks(target: Target) {
    if (!this.setupGeom) this.setupTagHits = [];
    if (this.geoms.length === 0 && !this.setupGeom) return;
    target.useMediaCoordinateSpace(({ context: ctx, mediaSize }) => {
      const c = this.colors;
      this.drawSetup(ctx, mediaSize.width, mediaSize.height);
      for (const g of this.ordered()) {
        const s = g.sig;
        ctx.save();
        ctx.globalAlpha = g.focused ? 1 : s.result === "no-fill" ? 0.45 : 0.7;
        const w = g.focused ? 1.5 : 1;
        // Entry is dotted until the order fills.
        hLine(ctx, g.xSig, g.xEnd, g.yEntry, withAlpha(c.text, 0.8), w, s.fillTime === null ? [2, 3] : []);
        hLine(ctx, g.xSig, g.xRisk, g.ySl, c.loss, w, [5, 4]);
        hLine(ctx, g.xSig, g.xEnd, g.yTp1, c.profit, w, [5, 4]);
        hLine(ctx, g.xSig, g.xEnd, g.yTp2, withAlpha(c.profit, 0.75), 1, [2, 3]);
        // Don't chase (Playbook › 4, step 1): the line Telegram's "skip if
        // price is already past X" refers to, for as long as the entry window is open.
        if (g.xChase !== null && g.yChase !== null) {
          hLine(ctx, g.xSig, g.xChase, g.yChase, withAlpha(c.loss, 0.9), 1, [1, 3]);
          // Its own tag on the emphasised signal; a hovered or selected one
          // lists it with its other prices instead (drawTags).
          if (g.focused && s.chase != null && this.focus.tags !== s.id) {
            this.drawChaseTag(ctx, g, s.chase, mediaSize.width, mediaSize.height);
          }
        }
        if (g.xTp1 !== null) dot(ctx, g.xTp1, g.yTp1, c.profit, c.surface);
        if (g.xExit !== null && g.yExit !== null) dot(ctx, g.xExit, g.yExit, this.toneColor(resultTone(s)), c.surface, 3.5);
        ctx.restore();
      }
      for (const g of this.ordered()) if (g.pill) this.drawPill(ctx, g);
      const tagged = this.geoms.find(g => g.sig.id === this.focus.tags);
      if (tagged) this.drawTags(ctx, tagged, mediaSize.width - this.insets.rightLane, mediaSize.height);
    });
  }

  /**
   * "Don't chase past X" beside the line's right end: above the line for a
   * buy (the side price would run to), below for a sell. Kept off the legend
   * and the waiting zone's tags: the other side of the line first, then slid
   * down past whatever it would cover.
   */
  private drawChaseTag(ctx: Ctx, g: Geometry, chase: number, width: number, height: number) {
    const c = this.colors;
    ctx.save();
    ctx.font = `600 10px ${c.font}`;
    const text = `Don't chase past ${this.format(chase)}`;
    const tw = ctx.measureText(text).width + 2 * TAG_PAD;
    const tx = Math.max(2, Math.min(g.xChase! + 4, width - this.insets.rightLane - 4 - tw));
    const yLine = g.yChase!;
    const above = yLine - TAG_H - 2, below = yLine + 2;
    const blockers: Rect[] = [...this.setupTagHits, ...(this.insets.legend ? [this.insets.legend] : [])];
    const clash = (y: number) => blockers.find(b => tx < b.x + b.w && tx + tw > b.x && y < b.y + b.h && y + TAG_H > b.y);
    const long = g.sig.action === "LONG";
    let ty = long ? above : below;
    if (clash(ty)) {
      const other = long ? below : above;
      if (!clash(other)) ty = other;
      else {
        for (let guard = 0; guard < 8; guard++) {
          const hit = clash(ty);
          if (!hit) break;
          ty = hit.y + hit.h + 2;
        }
      }
    }
    this.tag(ctx, tx, Math.min(ty, height - TAG_H - 2), tw, text, c.loss);
    ctx.restore();
  }

  /** [BUY | +1.2R] with a pointer to the trigger candle. */
  private drawPill(ctx: Ctx, g: Geometry) {
    const p = g.pill!;
    const s = g.sig;
    const c = this.colors;
    const color = s.result === "no-fill" ? c.muted : s.action === "LONG" ? c.profit : c.loss;
    const solid = s.source !== "replay";
    ctx.save();
    ctx.globalAlpha = g.focused ? 1 : 0.9;

    ctx.beginPath();
    const edge = p.down ? p.y + p.h : p.y;
    ctx.moveTo(p.tipX - POINTER, edge);
    ctx.lineTo(p.tipX + POINTER, edge);
    ctx.lineTo(p.tipX, p.down ? edge + POINTER : edge - POINTER);
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();

    // Whole pill: opaque surface so candles behind don't show through, tinted.
    ctx.beginPath();
    ctx.roundRect(p.x, p.y, p.w, p.h, 4);
    ctx.fillStyle = c.surface;
    ctx.fill();
    ctx.fillStyle = withAlpha(color, 0.12);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();

    // Direction half: solid when it was real (Telegram / live desk), light for a replay.
    ctx.beginPath();
    ctx.roundRect(p.x, p.y, p.split, p.h, p.w > p.split ? [4, 0, 0, 4] : 4);
    ctx.fillStyle = solid ? color : withAlpha(color, 0.28);
    ctx.fill();

    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    ctx.font = `700 10px ${c.font}`;
    ctx.fillStyle = solid ? inkOn(color) : color;
    ctx.fillText(p.left, p.x + p.split / 2, p.y + p.h / 2 + 0.5);
    ctx.font = `600 10px ${c.font}`;
    ctx.fillStyle = this.toneColor(resultTone(s));
    ctx.fillText(p.right, p.x + p.split + (p.w - p.split) / 2, p.y + p.h / 2 + 0.5);
    ctx.restore();
  }

  /**
   * SL / Entry / TP1 / TP2 tags at the box's right end, nudged apart so none
   * overlap. `width` is the usable width: price-line titles own the rest.
   */
  private drawTags(ctx: Ctx, g: Geometry, width: number, height: number) {
    const s = g.sig;
    const c = this.colors;
    const items = [
      { label: "TP2", price: s.tp2, y: g.yTp2, color: c.profit },
      { label: "TP1", price: s.tp1, y: g.yTp1, color: c.profit },
      { label: "Entry", price: s.entry, y: g.yEntry, color: c.text },
      { label: "SL", price: s.stopLoss, y: g.ySl, color: c.loss },
      // While the entry window is open: the don't-chase price joins the stack,
      // so it is nudged apart from Entry instead of covering it.
      ...(g.yChase !== null && s.chase != null ? [{ label: "Don't chase", price: s.chase, y: g.yChase, color: c.loss }] : []),
    ].sort((a, b) => a.y - b.y);

    ctx.save();
    ctx.font = `600 10px ${c.font}`;
    // Off-screen levels (the price scale doesn't stretch to every plan) pin to the edge.
    const texts = items.map(it => `${it.y < 0 ? "↑ " : it.y > height ? "↓ " : ""}${it.label} ${this.format(it.price)}`);
    const w = Math.max(...texts.map(t => ctx.measureText(t).width)) + 2 * TAG_PAD;
    // Right of the box (and of the BUY/SELL label, which a short trade ends
    // under). No room there: left of the signal instead, over older candles.
    const after = Math.max(g.xEnd, g.pill ? g.pill.x + g.pill.w : -Infinity) + 6;
    const before = Math.min(g.xSig, g.pill ? g.pill.x : Infinity) - 6 - w;
    const x = after + w <= width - 2 ? after : before >= 2 ? before : Math.max(2, width - w - 2);

    const half = TAG_H / 2, step = TAG_H + 2;
    const legend = this.insets.legend;
    const underLegend = legend !== null && x < legend.x + legend.w && x + w > legend.x;
    const top = (underLegend ? legend.y + legend.h + 2 : 2) + half, bottom = height - half - 2;
    const ys = items.map(it => Math.min(bottom, Math.max(top, it.y)));
    // Push overlapping tags down, then pull them back up from the bottom edge.
    for (let i = 1; i < ys.length; i++) ys[i] = Math.max(ys[i]!, ys[i - 1]! + step);
    ys[ys.length - 1] = Math.min(ys[ys.length - 1]!, bottom);
    for (let i = ys.length - 2; i >= 0; i--) ys[i] = Math.min(ys[i]!, ys[i + 1]! - step);

    items.forEach((it, i) => this.tag(ctx, x, ys[i]! - half, w, texts[i]!, it.color));
    ctx.restore();
  }

  /**
   * The zone the coin is waiting for: dashed edges and stop line from the 1h
   * close it was read on, and a tag in the empty space right of the last
   * candle ("Buy zone · watch the 1h close"), clear of the line-title lane.
   */
  private drawSetup(ctx: Ctx, width: number, height: number) {
    const hits: Rect[] = [];
    this.setupTagHits = hits;
    const s = this.setupGeom, z = this.setup;
    if (!s || !z) return;
    const c = this.colors;
    const color = z.direction === "LONG" ? c.profit : c.loss;
    const f = this.format;
    hLine(ctx, s.x, width, s.yHigh, withAlpha(color, 0.6), 1, [3, 3]);
    hLine(ctx, s.x, width, s.yLow, withAlpha(color, 0.6), 1, [3, 3]);
    if (s.yStop !== null) hLine(ctx, s.x, width, s.yStop, withAlpha(c.loss, 0.85), 1, [5, 4]);

    ctx.save();
    ctx.font = `600 10px ${c.font}`;
    // A tag never sits on another one. When its band overlaps a tag already
    // placed (the entry just under the zone's top edge covered "Buy zone …"),
    // it slides past it: down for tags that hang below their line, up for
    // tags that sit above it.
    const draw = (texts: string[], y: number, tone: string, allowLeft: boolean, slide: 1 | -1 = 1) => {
      const t = this.place(ctx, texts, s.x, width, allowLeft);
      if (!t) return;
      let yy = y;
      for (let guard = 0; guard < 8; guard++) {
        const clash = hits.find(h => t.x < h.x + h.w && t.x + t.w > h.x && yy < h.y + h.h && yy + TAG_H > h.y);
        if (!clash) break;
        yy = slide > 0 ? clash.y + clash.h + 2 : clash.y - TAG_H - 2;
      }
      this.tag(ctx, t.x, yy, t.w, t.text, tone);
      hits.push({ x: t.x, y: yy, w: t.w, h: TAG_H });
    };
    const below: 1 | -1 = z.direction === "LONG" ? 1 : -1; // the side of a line a long's entry/stop tag hangs on
    const above: 1 | -1 = z.direction === "LONG" ? -1 : 1;

    // Zone: its prices and what happens next, shortened to fit.
    const name = z.direction === "LONG" ? "Buy zone" : "Sell zone";
    const range = `${name} ${f(z.low)}–${f(z.high)}`;
    const legend = this.insets.legend;
    let top = Math.min(s.yLow, s.yHigh) + 3;
    if (legend && s.x < legend.x + legend.w && top < legend.y + legend.h) top = legend.y + legend.h + 3; // not under the legend
    // Only where it fits right of the zone start: left of it would cover the
    // newest candles, and the legend names the zone anyway.
    const waitFor = !z.armed ? "wait for price" : z.needsPattern ? "needs a 1h hammer or engulfing close" : "watch the 1h close";
    draw([`${range} · ${waitFor} · details`, `${range} · details`, range, name], top, color, false);

    // Stop: beyond its line, away from the zone (and from price).
    if (s.yStop !== null && z.stop !== null) {
      draw([`Stop ${f(z.stop)} · −1R`, `Stop ${f(z.stop)}`], z.direction === "LONG" ? s.yStop + 3 : s.yStop - TAG_H - 3, c.loss, true, below);
    }

    // Targets a signal here would get. Target 1 is kept in view; target 2 only when on screen.
    const p = z.plan;
    const reward = p ? planReward(p) : null;
    // The entry they are measured from, so the targets read as one plan: the
    // trigger candle's close (about price now), or the zone's near edge.
    if (p && s.yEntry !== null && s.yEntry >= 0 && s.yEntry <= height) {
      hLine(ctx, s.x, width, s.yEntry, withAlpha(c.text, 0.55), 1, [1, 3]);
      const entryText = `Entry ≈ ${f(p.entry)}`;
      // Risk : reward of the whole plan (half at each target): the box's number.
      const rr = `R:R 1:${reward!.full.toFixed(1)}`;
      draw([
        `${entryText} · ${rr} · ${z.armed ? "if the 1h candle closes about here" : "where price reaches the zone"}`,
        `${entryText} · ${rr}`,
        entryText,
      ], z.direction === "LONG" ? s.yEntry + 2 : s.yEntry - TAG_H - 2, c.text, true, below);
    }
    if (p) {
      const tp1 = `Target 1 ${p.tp1Exact ? "" : z.direction === "LONG" ? "≥ " : "≤ "}${f(p.tp1)}`;
      const tp2 = `Target 2 ${f(p.tp2)}`;
      const targets = [
        { y: s.yTp1, texts: [`${tp1} · ${reward!.r1.toFixed(1)}R · sell half`, tp1] },
        { y: s.yTp2, texts: [`${tp2} · ${reward!.r2.toFixed(1)}R · sell the rest`, tp2] },
      ];
      for (const t of targets) {
        if (t.y === null || t.y < 0 || t.y > height) continue;
        hLine(ctx, s.x, width, t.y, withAlpha(c.profit, 0.55), 1, [2, 4]);
        draw(t.texts, z.direction === "LONG" ? t.y - TAG_H - 2 : t.y + 2, c.profit, true, above);
      }
    }
    ctx.restore();
  }

  /**
   * Where a zone tag goes: right of the zone start `from`, the longest text
   * that fits clear of the line titles (they'd paint over it). No room there:
   * just left of the zone start if `allowLeft`, else nowhere. Uses the current font.
   */
  private place(ctx: Ctx, texts: string[], from: number, width: number, allowLeft: boolean): { x: number; w: number; text: string } | null {
    const lane = width - this.insets.rightLane - 4;
    const widthOf = (t: string) => ctx.measureText(t).width + 2 * TAG_PAD;
    for (const text of texts) {
      const w = widthOf(text);
      if (from + 4 + w <= lane) return { x: lane - w, w, text };
    }
    if (!allowLeft) return null;
    const text = texts[texts.length - 1]!;
    const w = widthOf(text);
    return { x: Math.max(2, from - 4 - w), w, text };
  }

  /** Tinted tag: opaque surface, a wash of `color`, `color` border and text. Caller sets the font. */
  private tag(ctx: Ctx, x: number, y: number, w: number, text: string, color: string) {
    ctx.beginPath();
    ctx.roundRect(x, y, w, TAG_H, 3);
    ctx.fillStyle = this.colors.surface;
    ctx.fill();
    ctx.fillStyle = withAlpha(color, 0.16);
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.textBaseline = "middle";
    ctx.textAlign = "left";
    ctx.fillStyle = color;
    ctx.fillText(text, x + TAG_PAD, y + TAG_H / 2 + 0.5);
  }
}
